/**
 * Реквизиты живой карточки ЭДО: «ключ — значение» по payload каждого типа (CardDetails),
 * смета суточных командировки (TripEstimateBlock) и риск-статус контрагента договора
 * (CounterpartyBadges / CounterpartyRisk). Плюс общие форматтеры даты для карточки.
 */
import { Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { pad2 } from '../../../lib/date'
import { fmtNum, timeAgo, ymd10 } from '../../../lib/format'
import { tone } from '../../../theme/tones'
import { Icon } from '../../../ui'
import type {
  BusinessTripPayload, ContractCountry, ContractPayload, CounterpartyCard, RecruitmentPayload, RequestCard, TimeOffPayload, TerminationPayload,
  VacationPayload,
} from '../../../lib/types'
import {
  CONTRACT_ENUM_LABEL, REC_EDUCATION, REC_ENUM_LABEL, REC_EXPERIENCE, REC_REASONS, REC_TEST_TYPES, recLabel,
} from '../../../lib/edo-labels'
import { counterpartyBadges } from '../counterpartyLookup'
import { EXIT_REASONS } from '../termination-types'
import { tripDestination } from '../processVM'
import { useCounterparty, useTripDestinations } from '../useEdo'
import { periodLabel } from '../itWorks/itWorksRules'

/** Device-local `DD.MM.YYYY HH:MM` from an ISO timestamp (robust without Intl). */
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso)
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}
/** `YYYY-MM-DD` для строки деталей — пустое значение показываем прочерком. */
const ymdRow = (v?: string | null) => ymd10(v) || '—'

/** Строка «ключ — значение» реквизитов: жирный текст, чтобы даты/часы читались с первого взгляда. */
export function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <XStack gap={10} alignItems="flex-start">
      {/* 124: на 104 «ПОДРАЗДЕЛЕНИЕ»/«ТЕХКОМПЕТЕНЦИИ» капсом рвались посреди слова. */}
      <Text fontSize={11} fontWeight="600" color="$muted" width={124} flexShrink={0} textTransform="uppercase" letterSpacing={0.3} paddingTop={2}>
        {k}
      </Text>
      <Text fontSize={14} fontWeight="700" color="$color" flex={1}>
        {v}
      </Text>
    </XStack>
  )
}

/** Key/value details from a live card (initiator + payload fields, spec §2.3). */
export function CardDetails({ card }: { card: RequestCard }) {
  const { t, lang } = useApp()
  // Справочник направлений — назвать страну командировки по GUID 1С; прочим карточкам не нужен.
  const { data: tripDests } = useTripDestinations(card.type === 'BUSINESS_TRIP')
  // Строка «ключ — значение» либо подзаголовок секции анкеты (подбор, 7 секций §6.2).
  const rows: ({ k: Localized; v: string } | { h: Localized })[] = [
    { k: { ru: 'Инициатор', en: 'Initiator' }, v: card.initiator?.fullName || '—' },
  ]
  if (card.employment) rows.push({ k: { ru: 'Место работы', az: 'İş yeri', en: 'Workplace' }, v: [card.employment.companyName, card.employment.position].filter(Boolean).join(' · ') })
  // Подача «от имени» (роль proxy): видно, кто фактически оформил заявку за сотрудника.
  if (card.submittedBy) {
    rows.push({
      k: { ru: 'Подал (от имени)', en: 'Submitted by (on behalf)' },
      v: card.submittedBy.fullName,
    })
  }
  // Версия документа (v1 = подача, каждая правка на доработке = +1): строка появляется только
  // когда документ реально дорабатывали — вечное «Версия 1» было бы шумом.
  if ((card.version ?? 1) > 1) {
    rows.push({
      k: { ru: 'Версия', en: 'Version' },
      v: `${card.version} (${t({ ru: 'изменения — во вкладке «История»', en: 'changes are in the History tab', az: 'dəyişikliklər «Tarixçə» bölməsindədir', ka: 'ცვლილებები — ჩანართში «ისტორია»' })})`,
    })
  }
  // Пустые поля не показываем; секция без единой строки не рисует и подзаголовок.
  const add = (k: Localized, v?: string | number | null) => {
    const s = v == null ? '' : String(v).trim()
    if (s) rows.push({ k, v: s })
  }
  const section = (h: Localized, fill: () => void) => {
    const at = rows.length
    rows.push({ h })
    fill()
    if (rows.length === at + 1) rows.pop()
  }
  let tripEstimate: BusinessTripPayload['estimate'] | undefined
  if (card.type === 'TIME_OFF') {
    const p = card.payload as Partial<TimeOffPayload>
    rows.push({ k: { ru: 'Начало', en: 'Start' }, v: fmtDateTime(p.timeFrom) })
    rows.push({ k: { ru: 'Окончание', en: 'End' }, v: fmtDateTime(p.timeTo) })
    if (p.durationHours != null) rows.push({ k: { ru: 'Длительность', en: 'Duration' }, v: `${fmtNum(Number(p.durationHours))} ${t({ ru: 'ч', en: 'h', az: 'saat' })}` })
    if (p.absenceReason) rows.push({ k: { ru: 'Причина', en: 'Reason' }, v: t(String(p.absenceReason)) })
    if (p.comment) rows.push({ k: { ru: 'Комментарий', en: 'Comment' }, v: String(p.comment) })
  } else if (card.type === 'TERMINATION') {
    const p = card.payload as Partial<TerminationPayload>
    add({ ru: 'Сотрудник', en: 'Employee' }, p.employeeName)
    rows.push({ k: { ru: 'Дата увольнения', en: 'Termination date' }, v: ymdRow(p.terminationDate) })
    add({ ru: 'Причина увольнения', en: 'Reason for leaving' }, p.reason)
    add({ ru: 'Основание из 1С', en: '1C termination reason' }, p.terminationReasonName)
    // Exit-интервью: сервер кладёт его в карточку только HR компании, CEO и admin.
    const ei = card.exitInterview
    if (ei) {
      const reasonLabel = EXIT_REASONS.find((r) => r.value === ei.reason)?.label
      rows.push({
        k: { ru: 'Exit-интервью', en: 'Exit interview', az: 'Exit-müsahibə' },
        v: ei.refused
          ? t({ ru: 'Сотрудник отказался или недоступен', en: 'Declined or unavailable', az: 'İmtina etdi və ya əlçatan deyil' })
          : reasonLabel ? t(reasonLabel) : '—',
      })
      add({ ru: 'Комментарий HR (интервью)', en: 'HR comment (interview)', az: 'HR şərhi (müsahibə)' }, ei.comment ?? undefined)
    }
  } else if (card.type === 'BUSINESS_TRIP') {
    const p = card.payload as Partial<BusinessTripPayload>
    const dest = tripDestination(p, tripDests)
    rows.push({ k: { ru: 'Направление', en: 'Destination' }, v: dest || '—' })
    // Полный адрес прибытия (v1.2) — нужен референту для трансфера; у старых заявок его нет.
    if (p.destinationAddress) rows.push({ k: { ru: 'Адрес прибытия', en: 'Arrival address' }, v: String(p.destinationAddress) })
    rows.push({ k: { ru: 'Даты', en: 'Dates' }, v: `${ymdRow(p.dateFrom)} – ${ymdRow(p.dateTo)}` })
    rows.push({ k: { ru: 'Выход на работу', en: 'Back to work' }, v: ymdRow(p.dateOfReturnToWork) })
    if (p.employeeNames?.length) rows.push({ k: { ru: 'Участники', en: 'Travellers' }, v: p.employeeNames.join(', ') })
    if (p.purpose) rows.push({ k: { ru: 'Цель', en: 'Purpose' }, v: String(p.purpose) })
    if (p.comment) rows.push({ k: { ru: 'Комментарий', en: 'Comment' }, v: String(p.comment) })
    tripEstimate = p.estimate ?? undefined
  } else if (card.type === 'FREEFORM') {
    // Произвольная форма: тема + текст. Сам маршрут рисует RouteTimeline по route[] с сервера —
    // дублировать список согласующих строкой здесь незачем.
    const p = card.payload as unknown as Record<string, unknown>
    rows.push({ k: { ru: 'Тема', en: 'Subject' }, v: String(p.title ?? '—') })
    rows.push({ k: { ru: 'Текст', en: 'Text' }, v: String(p.body ?? '—') })
  } else if (card.type === 'CONTRACT') {
    // Договор (ТЗ-2026-015). Согласующих рисует RouteTimeline, информируемых — отдельный блок
    // ниже (они не шаги маршрута), поэтому здесь только сам документ.
    const p = card.payload as unknown as Record<string, unknown>
    const enumRow = (k: Localized, code: unknown) =>
      rows.push({ k, v: code ? t(CONTRACT_ENUM_LABEL[String(code)] ?? { ru: String(code), en: String(code) }) : '—' })
    enumRow({ ru: 'Компания', en: 'Company' }, p.company)
    enumRow({ ru: 'Источник документа', en: 'Document source' }, p.source)
    // Номер присваивают юристы — пока его нет, строку не рисуем вовсе (прочерк читался бы
    // как «забыли заполнить», хотя пустой номер на согласовании — норма).
    if (p.docNo) rows.push({ k: { ru: 'Номер договора', en: 'Contract number' }, v: String(p.docNo) })
    enumRow({ ru: 'Тип документа', en: 'Document type' }, p.docType)
    // Уточнение и номер родителя — условные: показываем только заполненные, иначе у обычного
    // договора висели бы два прочерка подряд.
    if (p.otherType) rows.push({ k: { ru: 'Уточнение типа', en: 'Type details' }, v: String(p.otherType) })
    if (p.contractNo) {
      rows.push({ k: { ru: 'Основной договор', en: 'Master contract' }, v: String(p.contractNo) })
    }
    enumRow({ ru: 'Тип контракта', en: 'Contract type' }, p.serviceKind)
    rows.push({
      k: { ru: 'Приложение к договору', en: 'Contract appendix', az: 'Müqaviləyə əlavə' },
      v: typeof p.hasAdditionalAppendix === 'boolean'
        ? t(CONTRACT_ENUM_LABEL[String(p.hasAdditionalAppendix)])
        : t({ ru: 'Не указано', en: 'Not specified', az: 'Göstərilməyib' }),
    })
    enumRow({ ru: 'Длительность', en: 'Duration' }, p.duration)
    // Местный или зарубежный (12.08.2026) — отдельной строкой: от резидентства зависят и форма
    // договора, и налоги, и валютный контроль, а из одного лишь номера его не прочитать.
    enumRow({ ru: 'Тип контрагента', en: 'Counterparty type' }, p.counterpartyKind)
    const voen = String(p.counterpartyVoen ?? '').trim()
    const voenLabel = p.counterpartyKind === 'foreign' ? t({ ru: 'налоговый №', en: 'tax no.' }) : 'VÖEN'
    rows.push({
      k: { ru: 'Контрагент', en: 'Counterparty' },
      v: `${String(p.counterpartyName ?? '—')}${voen ? ` · ${voenLabel} ${voen}` : ''}`,
    })
    // Реквизиты зарубежного контрагента (19.10.2026) — у местных и у старых заявок их нет, строк тоже.
    if (p.counterpartyType) {
      enumRow({ ru: 'Юр- или физлицо', en: 'Legal entity or individual', az: 'Hüquqi və ya fiziki şəxs' }, p.counterpartyType)
    }
    const country = p.counterpartyCountry as ContractCountry | null | undefined
    if (country?.name) {
      rows.push({ k: { ru: 'Страна регистрации', en: 'Country of registration', az: 'Qeydiyyat ölkəsi' }, v: country.name })
    }
    if (p.counterpartyAddress) {
      rows.push({ k: { ru: 'Юридический адрес', en: 'Legal address', az: 'Hüquqi ünvan' }, v: String(p.counterpartyAddress) })
    }
    rows.push({ k: { ru: 'Предмет', en: 'Subject' }, v: String(p.subject ?? '—') })
    // Сумма необязательна — без неё «— AZN» читалось бы как ошибка, показываем просто прочерк.
    rows.push({ k: { ru: 'Сумма', en: 'Amount' }, v: p.amount == null ? '—' : `${String(p.amount)} ${String(p.currency ?? '')}` })
    const vatTerms = String(p.vatTerms ?? p.vatRate ?? '').trim()
    if (vatTerms) {
      rows.push({ k: { ru: 'Условия НДС', en: 'VAT terms', az: 'ƏDV şərtləri' }, v: vatTerms })
    }
    enumRow({ ru: 'Вид оплаты', en: 'Payment' }, p.paymentType)
    // Срок действия появляется только в финале — до него строки нет вовсе, а не с прочерком:
    // пустая «Срок действия: — » читалась бы как «забыли заполнить».
    if (p.dateFrom || p.dateTo) {
      rows.push({ k: { ru: 'Срок действия', en: 'Validity' }, v: `${ymdRow(p.dateFrom as string)} – ${ymdRow(p.dateTo as string)}` })
    }
    if (p.comment) rows.push({ k: { ru: 'Комментарий', en: 'Comment' }, v: String(p.comment) })
  } else if (card.type === 'IT_WORKS') {
    // IT-работы (ТЗ-2026-АКТ001 §2/§8.1): согласующие видят заявку целиком. Строки модулей и отчёты —
    // таблицей «Модули» ниже. Черновик неполный, доработка очищает поля в null — пустое не рисуем.
    const p = card.payload as unknown as Record<string, unknown>
    const itw = card.itWorks
    const dir = p.direction === 'GEORGIA' ? { ru: 'Грузия', en: 'Georgia', az: 'Gürcüstan' } : p.direction === 'BAKU' ? { ru: 'Баку', en: 'Baku', az: 'Bakı' } : null
    if (dir) rows.push({ k: { ru: 'Направление', en: 'Direction', az: 'İstiqamət' }, v: t(dir) })
    add({ ru: 'Подрядчик', en: 'Contractor', az: 'Podratçı' }, itw?.contractor && [itw.contractor.name, itw.contractor.taxId].filter(Boolean).join(' · '))
    const range = p.periodFrom || p.periodTo ? `${ymdRow(p.periodFrom as string)} – ${ymdRow(p.periodTo as string)}` : ''
    add(
      { ru: 'Отчётный период', en: 'Reporting period', az: 'Hesabat dövrü' },
      [typeof p.period === 'string' ? periodLabel(p.period, lang) : '', range && `(${range})`].filter(Boolean).join(' '),
    )
    add(
      { ru: 'Сумма к оплате', en: 'Amount to pay', az: 'Ödəniləcək məbləğ' },
      typeof p.amount === 'number' ? [fmtNum(p.amount, 2), p.currency].filter(Boolean).join(' ') : null,
    )
    add({ ru: 'Финальный утверждающий', en: 'Final approver', az: 'Yekun təsdiqləyici' }, itw?.finalApprover?.fullName)
    add({ ru: 'Получатели протокола', en: 'Protocol recipients', az: 'Protokolun alıcıları' }, (itw?.recipients ?? []).map((x) => x.fullName).join(', '))
    add({ ru: 'Комментарий', en: 'Comment', az: 'Şərh' }, typeof p.comment === 'string' ? p.comment : null)
  } else if (card.type === 'VACATION') {
    // Даты отпуска — здесь, а не в шапке: там их путали с датами документа (30.10.2026).
    const p = card.payload as Partial<VacationPayload>
    rows.push({ k: { ru: 'Даты отпуска', en: 'Vacation dates', az: 'Məzuniyyət tarixləri' }, v: `${ymdRow(p.startDate)} – ${ymdRow(p.endDate)}` })
    // Замещающий назначен при подаче (или поправлен HR) — у заявки без него строки нет.
    add({ ru: 'Замещающий', en: 'Substitute', az: 'Əvəz edən' }, card.deputy?.fullName)
    add({ ru: 'Комментарий', en: 'Comment', az: 'Şərh' }, p.comment)
  } else if (card.type === 'SICK_LEAVE') {
    const p = card.payload as unknown as Record<string, unknown>
    rows.push({ k: { ru: 'Период', en: 'Period' }, v: `${ymdRow(p.dateFrom as string)} – ${ymdRow(p.dateTo as string)}` })
    if (p.sheetNumber) rows.push({ k: { ru: 'Номер листа', en: 'Sheet no.' }, v: String(p.sheetNumber) })
    if (p.comment) rows.push({ k: { ru: 'Комментарий', en: 'Comment' }, v: String(p.comment) })
  } else if (
    card.type === 'PATERNITY_LEAVE' ||
    card.type === 'MATERNITY_LEAVE' ||
    card.type === 'CHILD_CARE_LEAVE'
  ) {
    // «Семейные» отпуска (ТЗ-2026-012/013/014): период отсутствия виден на всех стадиях, включая
    // подпись и согласованный документ. Уход за ребёнком бывает бессрочным (§4.1) → dateTo = null.
    const p = card.payload as unknown as Record<string, unknown>
    const to = ymd10(p.dateTo as string) || t({ ru: 'бессрочно', en: 'open-ended', az: 'müddətsiz' })
    rows.push({ k: { ru: 'Отсутствие с', en: 'Absent from' }, v: ymdRow(p.dateFrom as string) })
    rows.push({ k: { ru: 'Отсутствие по', en: 'Absent to' }, v: to })
    const child = p.child as Record<string, unknown> | undefined
    if (child) {
      const name = [child.lastName, child.firstName, child.middleName].filter(Boolean).join(' ')
      rows.push({ k: { ru: 'Ребёнок', en: 'Child' }, v: name || '—' })
      rows.push({ k: { ru: 'Дата рождения', en: 'Date of birth' }, v: ymdRow(child.birthDate as string) })
    }
    if (p.comment) rows.push({ k: { ru: 'Комментарий', en: 'Comment' }, v: String(p.comment) })
  } else if (card.type === 'HIRE') {
    const p = card.payload as unknown as Record<string, any>
    rows.push({ k: { ru: 'Способ приёма', en: 'Hire origin' }, v: p.hireOrigin === 'DIRECT' ? t({ ru: 'Без подбора', en: 'Without recruitment' }) : t({ ru: 'Из подбора', en: 'From recruitment' }) })
    if (p.hireOrigin === 'DIRECT' && p.externalApproval) {
      rows.push({ k: { ru: 'Кем согласовано', en: 'Approved by' }, v: p.externalApproval.approvedBy })
      rows.push({ k: { ru: 'Дата согласования', en: 'Approval date' }, v: ymdRow(p.externalApproval.approvedOn) })
      rows.push({ k: { ru: 'Основание приёма', en: 'Approval basis' }, v: p.externalApproval.basis })
    }
    rows.push({ k: { ru: 'Кандидат', en: 'Candidate' }, v: [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ') || p.candidate?.fullName || '—' })
    rows.push({ k: { ru: 'ФИН', en: 'FIN' }, v: p.fin || '—' })
    rows.push({ k: { ru: 'Дата приёма', en: 'Hire date' }, v: ymdRow(p.effectiveDate) })
    rows.push({ k: { ru: 'Позиция ШР', en: 'Staff position' }, v: p.staffPosition?.name || '—' })
    rows.push({ k: { ru: 'Ставки', en: 'Staff units' }, v: p.staffUnits || '—' })
    rows.push({ k: { ru: 'Вид занятости', en: 'Employment type' }, v: p.employmentType || '—' })
    for (const a of p.accruals ?? []) if (a.accrual) rows.push({ k: { ru: 'Начисление', en: 'Accrual' }, v: `${a.accrual.name} · ${a.amount} AZN` })
  } else if (card.type === 'RECRUITMENT') {
    // Полная анкета (ТЗ-2026-011 §6.2) — согласующие (рук./Рук.СП/ГД) должны видеть, что утверждают.
    const p = card.payload as Partial<RecruitmentPayload>
    // Enum-коды → подписи; справочные значения (reasons/experience/…) хранятся строками —
    // t(строка) переводит их на az по AZ_DB, для ru/en показывается как сохранено.
    const enumV = (c?: string | null) => (c ? t(REC_ENUM_LABEL[c] ?? String(c)) : '')
    section({ ru: 'Вакансия', en: 'Vacancy' }, () => {
      add({ ru: 'Название', en: 'Job title' }, p.jobTitle)
      add({ ru: 'Грейд', en: 'Grade' }, p.jobGradeNameRu ? t(String(p.jobGradeNameRu)) : p.jobGradeCode)
      add({ ru: 'Подразделение', en: 'Department' }, p.department)
      add({ ru: 'Подчинение', en: 'Reports to' }, p.managerName)
      add({ ru: 'Проект', en: 'Project' }, p.project)
      add({ ru: 'Место работы', en: 'Location' }, p.workLocation)
      add({ ru: 'Ставка', en: 'Rate' }, enumV(p.employmentRate))
      add({ ru: 'График', en: 'Schedule' }, enumV(p.workSchedule))
      add({ ru: 'Примечание', en: 'Note' }, p.note)
    })
    section({ ru: 'Обоснование', en: 'Justification' }, () => {
      add({ ru: 'Тип вакансии', en: 'Vacancy type' }, enumV(p.vacancyType))
      add({ ru: 'Штатная единица', en: 'Unit type' }, enumV(p.staffUnitType))
      add({ ru: 'Срок действия', en: 'Term' }, p.termOfValidity)
      add({ ru: 'Причины', en: 'Reasons' }, (p.reasons ?? []).map((r) => t(recLabel(REC_REASONS, String(r)))).join(', '))
      add({ ru: 'Кого замещает', en: 'Replaces' }, p.replacesName)
      if (p.salaryFrom != null || p.salaryTo != null) {
        add(
          { ru: 'Вилка зарплаты', en: 'Salary range' },
          `${p.salaryFrom ?? '—'} – ${p.salaryTo ?? '—'}${p.currency ? ` ${p.currency}` : ''}`,
        )
      }
      add({ ru: 'Дата выхода', en: 'Start date' }, ymd10(p.plannedStartDate))
      add({ ru: 'Иные причины', en: 'Other reasons' }, p.otherReasons)
      add({ ru: 'Причина (текст)', en: 'Reason (free text)' }, p.reason)
    })
    section({ ru: 'Требования', en: 'Requirements' }, () => {
      add({ ru: 'Возраст', en: 'Age limits' }, p.ageLimit)
      add({ ru: 'Опыт', en: 'Experience' }, p.experience ? t(recLabel(REC_EXPERIENCE, String(p.experience))) : '')
      add({ ru: 'Образование', en: 'Education' }, p.education ? t(recLabel(REC_EDUCATION, String(p.education))) : '')
      add({ ru: 'Сертификаты', en: 'Certificates' }, p.certificates)
      add(
        { ru: 'Языки', en: 'Languages' },
        (p.languages ?? [])
          .filter((l) => l?.name)
          .map((l) => (l.level ? `${l.name} (${l.level})` : l.name))
          .join(', '),
      )
      add({ ru: 'Техкомпетенции', en: 'Tech skills' }, p.techSkills)
      add({ ru: 'Личные качества', en: 'Personal qualities' }, p.personalQualities)
    })
    section({ ru: 'Условия', en: 'Conditions' }, () => {
      const conds = [
        p.travelDomestic ? t({ ru: 'Командировки по стране', en: 'Domestic travel' }) : '',
        p.travelInternational ? t({ ru: 'Командировки за рубеж', en: 'International travel' }) : '',
        p.ownCar ? t({ ru: 'Личный автомобиль', en: 'Own car' }) : '',
        p.driverLicense
          ? t({ ru: 'Водительское удостоверение', en: 'Driver license' }) +
            (p.driverCategory ? ` (${p.driverCategory})` : '')
          : '',
      ].filter(Boolean)
      add({ ru: 'Условия', en: 'Conditions' }, conds.join(', '))
      add({ ru: 'Иные условия', en: 'Other conditions' }, p.otherConditions)
    })
    section({ ru: 'Проверка', en: 'Assessment' }, () => {
      if (p.testingRequired) {
        add(
          { ru: 'Тестирование', en: 'Testing' },
          (p.testTypes ?? []).map((x) => t(recLabel(REC_TEST_TYPES, String(x)))).join(', ') || t({ ru: 'Требуется', en: 'Required' }),
        )
      }
      add({ ru: 'Иные навыки', en: 'Other skills' }, p.otherSkills)
    })
    section({ ru: 'Собеседования', en: 'Interviews' }, () => {
      add(
        { ru: 'Участники', en: 'Panel' },
        (p.interviewers ?? [])
          .filter((i) => i?.fullName)
          .map((i) => (i.position ? `${i.fullName} — ${i.position}` : i.fullName))
          .join('\n'),
      )
    })
    if (p.comment) {
      section({ ru: 'Комментарий', en: 'Comment' }, () => add({ ru: 'Комментарий', en: 'Comment' }, p.comment))
    }
    const cand = p.candidate
    if (cand) {
      // Блок появляется после закрытия вакансии (снапшот из формы закрытия ОИ/HR).
      section({ ru: 'Кандидат', en: 'Candidate' }, () => {
        add({ ru: 'ФИО', en: 'Full name' }, cand.fullName)
        add({ ru: 'Телефон', en: 'Phone' }, cand.phone)
        add({ ru: 'Email', en: 'Email' }, cand.email)
        add({ ru: 'Должность', en: 'Position' }, cand.position)
        if (cand.agreedSalary != null) {
          add(
            { ru: 'Согласованная зарплата', en: 'Agreed salary' },
            `${cand.agreedSalary}${p.currency ? ` ${p.currency}` : ''}`,
          )
        }
        add({ ru: 'Дата выхода', en: 'Start date' }, ymd10(cand.startDate))
        add({ ru: 'Источник', en: 'Source' }, cand.source)
        add({ ru: 'Примечание', en: 'Note' }, cand.note)
      })
    }
  }
  return (
    <YStack gap={10}>
      {rows.map((r, i) =>
        'h' in r ? (
          <Text key={i} fontSize={10} fontWeight="700" color="$muted" textTransform="uppercase" letterSpacing={0.4} marginTop={i ? 6 : 0}>
            {t(r.h)}
          </Text>
        ) : (
          <DetailRow key={i} k={t(r.k)} v={r.v} />
        ),
      )}
      {tripEstimate && <TripEstimateBlock est={tripEstimate} />}
    </YStack>
  )
}

/**
 * Пометки о контрагенте — общие для формы подачи и карточки заявки. Пилюли в стиле
 * EdoStatusBadge; красные (риск-группа, недействующий) дополнительно обведены solid-рамкой,
 * чтобы результат обмена с госпорталом нельзя было не заметить. Серая оговорка о свежести —
 * не статус, а сноска: обычным текстом, своей строкой.
 */
export function CounterpartyBadges({ card }: { card: CounterpartyCard }) {
  const { t } = useApp()
  const badges = counterpartyBadges(card)
  return (
    <XStack gap={6} flexWrap="wrap" alignItems="center">
      {badges.map((b, i) => {
        if (b.tone === 'gray') {
          return (
            <Text key={i} fontSize={12} color="$muted" flexBasis="100%">
              {t(b.label)}
            </Text>
          )
        }
        const tn = tone(b.tone)
        return (
          <XStack key={i} backgroundColor={tn.soft} borderRadius={999} paddingHorizontal={10}
            paddingVertical={4} borderWidth={1}
            borderColor={b.tone === 'red' ? tn.solid : 'transparent'}>
            <Text fontSize={11} fontWeight={b.tone === 'red' ? '700' : '600'} color={tn.text}>
              {t(b.label)}
            </Text>
          </XStack>
        )
      })}
    </XStack>
  )
}

/**
 * Риск-статус контрагента в карточке договора (ТЗ-2026-015 §7.2). Заявка хранит только ВÖİN,
 * поэтому спрашиваем реестр на момент просмотра — юрист решает по свежим данным, а не по тому,
 * что видел инициатор при подаче. Справочный блок: не нашли или реестр молчит — так и пишем,
 * согласование это не блокирует (как и сама проверка в форме).
 *
 * У зарубежного контрагента блока нет вовсе: реестр Госналоговой ведёт только резидентов, и
 * «проверить не удалось» про нерезидента — не новость, а шум. У заявок до 12.08.2026 признака в
 * payload нет — они все местные (ВÖİN тогда был обязателен), поэтому неизвестный тип = местный.
 */
export function CounterpartyRisk({ contract }: { contract: ContractPayload }) {
  const { t } = useApp()
  const voen = contract?.counterpartyKind === 'foreign' ? '' : (contract?.counterpartyVoen ?? '')
  const q = useCounterparty(voen)
  if (!voen) return null
  const note = (msg: Localized) => <Text fontSize={12} color="$muted">{t(msg)}</Text>
  if (q.isPending) return note({ ru: 'Проверяем контрагента в реестре Госналоговой…', en: 'Checking the tax registry…' })
  if (!q.data) {
    return note({
      ru: 'Риск-статус контрагента сейчас проверить не удалось.',
      en: 'Could not check the counterparty risk status right now.',
    })
  }
  return (
    <YStack gap={4}>
      <CounterpartyBadges card={q.data} />
      {!!q.data.checkedAt && (
        <Text fontSize={11} color="$muted">
          {t({ ru: 'Сверено с реестром', en: 'Checked against the registry' })}: {timeAgo(q.data.checkedAt)}
        </Text>
      )}
    </YStack>
  )
}

/**
 * Суточные командировки с разбивкой по участникам (ТЗ-2026-003 v1.2 §4.2) — чтобы CEO утверждал
 * расходы не вслепую. Норма зеркальная из регистра 1С; окончательные суммы — у бухгалтера и CFO.
 */
function TripEstimateBlock({ est }: { est: NonNullable<BusinessTripPayload['estimate']> }) {
  const { t } = useApp()
  const loc = t({ ru: 'ru-RU', en: 'en-US' })
  const money = (n: number) => `${n.toLocaleString(loc)} ${est.currency}`
  return (
    <YStack
      gap={7}
      marginTop={4}
      backgroundColor="$primaryBg"
      borderWidth={1}
      borderColor="$primary"
      borderRadius={7}
      paddingHorizontal={12}
      paddingVertical={10}
    >
      <XStack alignItems="center" gap={8}>
        <Icon name="wallet" size={15} color="$primary" />
        <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
          {t({ ru: 'Суточные', en: 'Per-diem' })}
        </Text>
        <Text fontSize={11} color="$muted">
          {est.dailyAmount} {est.currency}
          {t({ ru: '/сут × ', en: '/day × ', az: '/gün × ' })}
          {est.days}
          {t({ ru: ' дн.', en: ' d', az: ' gün' })}
        </Text>
      </XStack>

      {/* Разбивка по каждому сотруднику. Старые заявки (до v1.2) снапшота строк не имеют. */}
      {est.perEmployee?.map((l) => (
        <XStack key={l.employeeId} alignItems="center" gap={8}>
          <Text fontSize={12} color="$muted" flex={1} numberOfLines={1}>
            {l.name}
          </Text>
          <Text fontSize={12} fontWeight="600" color="$color">
            {money(l.amount)}
          </Text>
        </XStack>
      ))}

      <XStack alignItems="center" gap={8} borderTopWidth={1} borderColor="$primary" paddingTop={7}>
        <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
          {t({ ru: 'ИТОГО', en: 'TOTAL' })}
        </Text>
        <Text fontSize={13} fontWeight="700" color="$primary">
          {money(est.amount)}
        </Text>
      </XStack>

      <Text fontSize={11} color="$muted">
        {t({
          ru: 'Предварительно; окончательные суммы — у бухгалтера и CFO',
          en: 'Preliminary; final figures are set by Accounting and the CFO',
        })}
      </Text>
    </YStack>
  )
}
